import React, { useCallback, useEffect, useRef } from 'react';

import { graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import type { Skill } from '@atlassian/rovo-platform-types/skills';
import {
	type BrowseItem,
	InlineBrowse,
	type InlineBrowseIconProps,
} from '@atlassian/rovo-platform-ui-components/inline-browse';
import { getIconColor } from '@atlassian/rovo-platform-ui-components/skills/skill-colors';
import {
	DefaultSkillIcon,
	skillIconMap,
} from '@atlassian/rovo-platform-ui-components/skills/skill-icons';
import { SkillHoverCardContent } from '@atlassian/rovo-platform-ui-components/skills/SkillHoverCardContent';
import { useInfiniteScroll } from '@atlassian/rovo-platform-ui-components/useInfiniteScroll';

import { parseUniversalSkillEdges } from '../../utils/parse-skill';
import { useRovoSkillsInvalidation } from '../rovo-skills-config-provider';

import type { browseSkills_AtlassianRovoSkills_BrowseSkillsLegacy_preloadedRef$key } from './__generated__/browseSkills_AtlassianRovoSkills_BrowseSkillsLegacy_preloadedRef.graphql';
import type { browseSkills_AtlassianRovoSkills_BrowseSkillsUnified_preloadedRef$key } from './__generated__/browseSkills_AtlassianRovoSkills_BrowseSkillsUnified_preloadedRef.graphql';
import type { browseSkills_Query } from './__generated__/browseSkills_Query.graphql';
import type { browseSkills_UnifiedQuery } from './__generated__/browseSkills_UnifiedQuery.graphql';
import type {
	browseSkillsPaginationQuery,
	browseSkillsPaginationQuery$variables,
} from './__generated__/browseSkillsPaginationQuery.graphql';
import type {
	browseSkillsUnifiedPaginationQuery,
	browseSkillsUnifiedPaginationQuery$variables,
} from './__generated__/browseSkillsUnifiedPaginationQuery.graphql';

type BrowseSkillsProps = {
	siteId: string;
	searchKeyword: string;
	closeInlineBrowse: () => void;
	onSelectSkill: (skill: Skill) => void;
	onSkillHover?: (skillName: string | null) => void;
};

const PAGE_SIZE = 30;

type BrowseSkillsViewProps = BrowseSkillsProps & {
	skills: Skill[];
	hasNext: boolean;
	isLoadingNext: boolean;
	loadNext: (count: number) => void;
};

const BrowseSkillsView = ({
	skills,
	hasNext,
	isLoadingNext,
	loadNext,
	closeInlineBrowse,
	onSelectSkill,
	onSkillHover,
}: BrowseSkillsViewProps): React.ReactNode => {
	const skillSlug = React.useMemo(() => new Map(skills.map((s) => [s.slug, s])), [skills]);

	const browseItems: BrowseItem[] = React.useMemo(
		() =>
			skills.map((skill) => ({
				id: skill.slug,
				name: skill.name,
				description: skill.description,
				iconName: skill.iconName,
				iconColor: getIconColor(skill.color),
				product: skill.product,
			})),
		[skills],
	);

	const renderSkillHoverCardContent = (browseItem: BrowseItem) => {
		const skill = skillSlug.get(browseItem.id);
		if (!skill) {
			return null;
		}
		const { slug: _slug, ...skillContentProps } = skill;
		return <SkillHoverCardContent {...skillContentProps} />;
	};

	const handleLoadMore = useCallback(() => {
		if (isLoadingNext || !hasNext) {
			return;
		}
		loadNext(PAGE_SIZE);
	}, [isLoadingNext, hasNext, loadNext]);

	const listEndRef = useInfiniteScroll({
		onScrollTo: handleLoadMore,
	});

	return (
		<InlineBrowse
			browseItems={browseItems}
			onSelectBrowseItem={(browseItem) => {
				const skill = skillSlug.get(browseItem.id);
				if (skill) {
					onSelectSkill(skill);
				}
			}}
			onBrowseItemHover={(browseItem) => {
				onSkillHover?.(browseItem ? browseItem.name : null);
			}}
			onClose={closeInlineBrowse}
			iconMap={skillIconMap as Record<string, React.ComponentType<InlineBrowseIconProps>>}
			defaultIcon={DefaultSkillIcon as React.ComponentType<InlineBrowseIconProps>}
			renderHoverCardContent={renderSkillHoverCardContent}
			listFooterLoading={isLoadingNext}
			listEndRef={hasNext ? listEndRef : undefined}
		/>
	);
};

const BrowseSkillsLegacy = (props: BrowseSkillsProps): React.ReactNode => {
	const { siteId, searchKeyword } = props;
	const { skillsRevision } = useRovoSkillsInvalidation();
	const preloadedRef = useLazyLoadQuery<browseSkills_Query>(
		graphql`
			query browseSkills_Query(
				$first: Int
				$after: String
				$dimensionIds: [ID!]
				$searchKeyword: String
				$relevantFor: [GraphIntegrationDirectoryItemType!] = [SKILL]
				$cloudId: ID!
				$surface: GraphIntegrationSurface
			) {
				...browseSkills_AtlassianRovoSkills_BrowseSkillsLegacy_preloadedRef
					@arguments(
						first: $first
						after: $after
						dimensionIds: $dimensionIds
						searchKeyword: $searchKeyword
						relevantFor: $relevantFor
						cloudId: $cloudId
						surface: $surface
					)
			}
		`,
		{
			first: PAGE_SIZE,
			cloudId: siteId,
			surface: 'ROVO',
			relevantFor: ['SKILL'],
			searchKeyword: null,
		},
	);

	const skipSearchRefetchOnMount = useRef(true);
	const latestSearchKeywordRef = useRef(searchKeyword);

	useEffect(() => {
		latestSearchKeywordRef.current = searchKeyword;
	}, [searchKeyword]);

	// Use pagination fragment for infinite loading
	const { data, hasNext, isLoadingNext, loadNext, refetch } = usePaginationFragment<
		browseSkillsPaginationQuery,
		browseSkills_AtlassianRovoSkills_BrowseSkillsLegacy_preloadedRef$key
	>(
		/* eslint-disable @atlassian/relay/unused-fields -- fields consumed by parseSkillEdges */
		graphql`
			fragment browseSkills_AtlassianRovoSkills_BrowseSkillsLegacy_preloadedRef on Query
			@argumentDefinitions(
				first: { type: "Int", defaultValue: 30 }
				after: { type: "String" }
				dimensionIds: { type: "[ID!]" }
				searchKeyword: { type: "String" }
				relevantFor: { type: "[GraphIntegrationDirectoryItemType!]", defaultValue: [SKILL] }
				cloudId: { type: "ID!" }
				surface: { type: "GraphIntegrationSurface" }
			)
			@refetchable(queryName: "browseSkillsPaginationQuery") {
				graphIntegration_componentDirectoryItems(
					first: $first
					after: $after
					dimensionIds: $dimensionIds
					searchKeyword: $searchKeyword
					relevantFor: $relevantFor
					cloudId: $cloudId
					surface: $surface
				)
					@optIn(to: "GraphIntegrationItemsQuery")
					@connection(key: "browseSkills_graphIntegration_componentDirectoryItems") {
					edges {
						node {
							__typename
							... on GraphIntegrationSkillDirectoryItem {
								id
								slashCommand
								name
								description
								iconUrl
								integrationKey {
									displayName
									iconUrl
								}
								color
							}
						}
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						startCursor
						endCursor
					}
					error {
						message
						extensions {
							statusCode
							errorType
						}
					}
				}
			}
		`,
		/* eslint-enable @atlassian/relay/unused-fields */
		preloadedRef,
	);

	useEffect(() => {
		if (skipSearchRefetchOnMount.current) {
			skipSearchRefetchOnMount.current = false;
			return;
		}
		const trimmed = searchKeyword.trim();
		const variables: Partial<browseSkillsPaginationQuery$variables> = {
			searchKeyword: trimmed.length > 0 ? trimmed : null,
		};
		refetch(variables);
	}, [searchKeyword, refetch]);

	useEffect(() => {
		if (skillsRevision === 0) {
			return;
		}
		const trimmed = latestSearchKeywordRef.current.trim();
		const variables: Partial<browseSkillsPaginationQuery$variables> = {
			searchKeyword: trimmed.length > 0 ? trimmed : null,
		};
		refetch(variables, { fetchPolicy: 'network-only' });
	}, [refetch, skillsRevision]);

	const skillsResponse = data?.graphIntegration_componentDirectoryItems;

	if (skillsResponse && 'error' in skillsResponse && skillsResponse.error) {
		const err = skillsResponse.error as {
			message?: string | null;
			extensions?: ReadonlyArray<{
				statusCode?: number | null;
				errorType?: string | null;
			} | null> | null;
		};
		const errExtensions = err.extensions
			? `[${err.extensions
					?.map(
						(ext: { statusCode?: number | null; errorType?: string | null } | null) =>
							`${ext?.statusCode} - ${ext?.errorType}`,
					)
					?.join(', ')}] `
			: '';
		throw new Error(
			err.message || errExtensions ? `${errExtensions}${err.message}` : 'Failed to load skills',
		);
	}

	const skills: Skill[] = React.useMemo(
		() => parseUniversalSkillEdges(skillsResponse?.edges ?? null),
		[skillsResponse?.edges],
	);

	return (
		<BrowseSkillsView
			{...props}
			skills={skills}
			hasNext={hasNext}
			isLoadingNext={isLoadingNext}
			loadNext={loadNext}
		/>
	);
};

const BrowseSkillsUnified = (props: BrowseSkillsProps): React.ReactNode => {
	const { siteId, searchKeyword } = props;
	const { skillsRevision } = useRovoSkillsInvalidation();
	const trimmedSearch = searchKeyword.trim();
	const latestSearchKeywordRef = useRef(searchKeyword);

	useEffect(() => {
		latestSearchKeywordRef.current = searchKeyword;
	}, [searchKeyword]);

	const preloadedRef = useLazyLoadQuery<browseSkills_UnifiedQuery>(
		graphql`
			query browseSkills_UnifiedQuery(
				$first: Int
				$after: String
				$cloudId: ID!
				$searchKeyword: String
			) {
				...browseSkills_AtlassianRovoSkills_BrowseSkillsUnified_preloadedRef
					@arguments(first: $first, after: $after, cloudId: $cloudId, searchKeyword: $searchKeyword)
			}
		`,
		{
			first: PAGE_SIZE,
			cloudId: siteId,
			searchKeyword: trimmedSearch.length > 0 ? trimmedSearch : null,
		},
	);

	const { data, hasNext, isLoadingNext, loadNext, refetch } = usePaginationFragment<
		browseSkillsUnifiedPaginationQuery,
		browseSkills_AtlassianRovoSkills_BrowseSkillsUnified_preloadedRef$key
	>(
		/* eslint-disable @atlassian/relay/unused-fields -- fields consumed by parseUniversalSkillEdges */
		graphql`
			fragment browseSkills_AtlassianRovoSkills_BrowseSkillsUnified_preloadedRef on Query
			@argumentDefinitions(
				first: { type: "Int", defaultValue: 30 }
				after: { type: "String" }
				cloudId: { type: "ID!" }
				searchKeyword: { type: "String" }
			)
			@refetchable(queryName: "browseSkillsUnifiedPaginationQuery") {
				convoai_availableSkills(
					first: $first
					after: $after
					cloudId: $cloudId
					searchKeyword: $searchKeyword
				)
					@optIn(to: "ConvoAiAvailableSkills")
					@connection(key: "browseSkills__convoai_availableSkills") {
					edges {
						node {
							__typename
							id
							name
							description
							displayName
							slashCommand
							iconUrl
							iconKey
							color
							helpText
							source
							tags
							category
							integrationKeys
							integrations {
								key
								id
								displayName
								iconUrl
							}
							version
						}
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						startCursor
						endCursor
					}
					totalCount
					metadata {
						anthropicCount
						stratusCount
						isDegraded
					}
					errors {
						message
					}
				}
			}
		`,
		preloadedRef,
	);

	useEffect(() => {
		if (skillsRevision === 0) {
			return;
		}
		const trimmed = latestSearchKeywordRef.current.trim();
		const variables: Partial<browseSkillsUnifiedPaginationQuery$variables> = {
			searchKeyword: trimmed.length > 0 ? trimmed : null,
		};
		refetch(variables, { fetchPolicy: 'network-only' });
	}, [refetch, skillsRevision]);

	const skillsResponse = data?.convoai_availableSkills;

	if (
		skillsResponse &&
		'errors' in skillsResponse &&
		skillsResponse.errors &&
		skillsResponse.errors.length > 0
	) {
		const errorMessages = skillsResponse.errors
			.map((err: { message?: string | null } | null) => err?.message)
			.join(', ');
		throw new Error(errorMessages || 'Failed to load skills');
	}

	const skills: Skill[] = React.useMemo(
		() => parseUniversalSkillEdges(skillsResponse?.edges ?? null),
		[skillsResponse?.edges],
	);

	return (
		<BrowseSkillsView
			{...props}
			skills={skills}
			hasNext={hasNext}
			isLoadingNext={isLoadingNext}
			loadNext={loadNext}
		/>
	);
};

export const BrowseSkills = (props: BrowseSkillsProps): React.ReactNode => {
	if (fg('rovo_chat_unified_skills_agg')) {
		return <BrowseSkillsUnified {...props} />;
	}
	return <BrowseSkillsLegacy {...props} />;
};
